import { PaymentMethod } from '@easy-expense/data-schema-v2';
import Data, { deletedUsageSubHeader } from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { Layout } from '@easy-expense/ui-web-core';
import { usePaymentMethodUsageCounts } from '@easy-expense/utils-client';
import React from 'react';

import { LabelTextField } from '../../LabelTextField.component';
import { useDeletedInUse } from '../../Shared/useDeletedInUse';

export const PaymentMethodField: React.FC<
  React.PropsWithChildren<{
    initialPaymentMethodKey?: string | null;
    onChange: (paymentMethodKey: string) => void;
  }>
> = ({ initialPaymentMethodKey, onChange }) => {
  const paymentMethods: PaymentMethod[] = Data.paymentMethods.use();
  const [paymentMethodKey, setPaymentMethodKey] = React.useState(initialPaymentMethodKey ?? '');

  const usageCounts = usePaymentMethodUsageCounts();
  const deletedInUse = useDeletedInUse(usageCounts, Data.paymentMethods.getByKey, paymentMethods);

  // The tally only covers expenses this viewer has loaded, so it can miss the one being edited.
  // Its own payment method must always stay an option, or the field reads as though the expense was
  // never paid with anything.
  const deletedPaymentMethods = React.useMemo(() => {
    const selected = Data.paymentMethods.getByKey(paymentMethodKey);
    const isMissing =
      !!selected &&
      selected.entityState !== 'active' &&
      !deletedInUse.some(({ entity }) => entity.key === selected.key);

    if (!selected || !isMissing) {
      return deletedInUse;
    }

    return [{ entity: selected, usageCount: usageCounts[selected.key] ?? 1 }, ...deletedInUse];
  }, [deletedInUse, paymentMethodKey, usageCounts]);

  function onPaymentMethodChange(value: string) {
    setPaymentMethodKey(value);
    onChange(value);
  }

  return (
    <LabelTextField label="Payment Method (optional)" active={false}>
      <Layout.Row
        justify
        center
        style={{
          fontFamily: 'Poppins_Light',
          position: 'relative',
        }}
      >
        <Icon name="card-outline" size={20} style={{ paddingRight: 10 }} />
        <select
          value={paymentMethodKey}
          onChange={(event) => onPaymentMethodChange(event.target.value)}
          style={{
            border: 'none',
            width: '100%',
            background: 'transparent',
            zIndex: 1,
          }}
        >
          <option value="">Select a payment method</option>
          {paymentMethods.map((paymentMethod) => (
            <option key={paymentMethod.key} value={paymentMethod.key}>
              {paymentMethod.value.name}
            </option>
          ))}
          {/* Deleted payment methods are still offered while expenses are filed against them, so
              this expense can keep or return to the card it was actually paid with. */}
          {deletedPaymentMethods.length ? (
            <optgroup label={getTranslation('Deleted')}>
              {deletedPaymentMethods.map(({ entity, usageCount }) => (
                <option key={entity.key} value={entity.key}>
                  {`${entity.value.name} · ${deletedUsageSubHeader(usageCount)}`}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
        <span
          style={{
            position: 'absolute',
            left: 'calc(100% - 20px)',
            top: 2,
            zIndex: 0,
          }}
        >
          <Icon name="chevron-down" size={16} iconColor="primary" />
        </span>
      </Layout.Row>
    </LabelTextField>
  );
};
